import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import { useAppDispatch } from '../store/redux-hooks';
import { updateEmployeePersonalDetails } from '../pages/admin-settings/employee/EmployeeHelper';
import { fetchEmployeeDetails } from '../features/employeeDataSlice';

const PersonalDetailsEdit = ({
  user,
  setEnableEditValue,
  refreshData = () => {},
  setShowLoader,
}: any) => {
  const dispatch = useAppDispatch();
  const [enableEdit, setEnableEdit] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    maritalStatus: '',
    mobileNumber: '',
    emailId: '',
    candidateId: '',
  });

  const [originalData, setOriginalData] = useState(formData);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    setEnableEdit(true);

    const initialData = {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      gender: user?.gender?.toString()?.toLowerCase() || '',
      maritalStatus: user?.maritalStatus?.toString()?.toLowerCase() || '',
      mobileNumber: user?.mobileNumber || '',
      emailId: user?.emailId || '',
      candidateId: user?.candidateId || '',
    };

    setFormData(initialData);
    setOriginalData(initialData);
  }, [user]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      const hasChanges = Object.keys(updated).some(
        (key) =>
          updated[key as keyof typeof updated] !==
          originalData[key as keyof typeof originalData]
      );
      setIsEdited(hasChanges);
      return updated;
    });
  };

  const validateFirstName = (name: string) => {
    if (!name || name.trim().length < 2) return 'Enter at least 2 characters';
    if (!/^[a-zA-Z ]+$/.test(name.trim())) return 'Please enter alphabets only';
    return '';
  };

  const validateLastName = (name: string) => {
    if (!name || name.trim() === '') return '';
    if (name.trim().length < 2) return 'Enter at least 2 characters';
    if (!/^[a-zA-Z ]+$/.test(name.trim())) return 'Please enter alphabets only';
    return '';
  };

  const isValidPhone = (num: string) => /^[6-9]\d{9}$/.test(num);
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.(com|co|in)$/i.test(email);

  const handleCancelClick = () => {
    setEnableEdit(false);
    setEnableEditValue(false);
    setFormData(originalData);
    setIsEdited(false);
  };

  const handleSaveClick = async () => {
    const firstNameError = validateFirstName(formData.firstName);
    const lastNameError = validateLastName(formData.lastName);

    if (
      firstNameError ||
      lastNameError ||
      !isValidPhone(formData.mobileNumber) ||
      !isValidEmail(formData.emailId) ||
      !formData.gender ||
      !formData.maritalStatus
    ) {
      setAlertMessage('Please fill all missing fields correctly.');
      setShowAlert(true);
      return;
    }

    setShowLoader(true);
    await dispatch(updateEmployeePersonalDetails(formData));
    await dispatch(fetchEmployeeDetails({ id: formData.candidateId }));
    setShowLoader(false);
    setEnableEdit(false);
    setEnableEditValue(false);
    setIsEdited(false);
    refreshData?.();
  };

  return (
    <>
      <Dialog
        open={enableEdit}
        onClose={handleCancelClick}
        PaperProps={{ style: { minWidth: 500, maxWidth: 500 } }}
      >
        <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>
          Edit Personal Details
        </DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={!!validateFirstName(formData.firstName)}
            helperText={validateFirstName(formData.firstName) || ' '}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={!!validateLastName(formData.lastName)}
            helperText={validateLastName(formData.lastName) || ' '}
            fullWidth
            margin="dense"
          />
          <TextField
            select
            label="Gender"
            value={formData.gender}
            onChange={(e) => handleChange('gender', e.target.value)}
            fullWidth
            margin="dense"
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            select
            label="Marital Status"
            value={formData.maritalStatus}
            onChange={(e) => handleChange('maritalStatus', e.target.value)}
            fullWidth
            margin="dense"
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="widowed">Widowed</MenuItem>
          </TextField>
          <TextField
            label="Contact Number"
            value={formData.mobileNumber}
            onChange={(e) => handleChange('mobileNumber', e.target.value)}
            error={!isValidPhone(formData.mobileNumber)}
            helperText={
              !isValidPhone(formData.mobileNumber)
                ? 'Enter a valid 10-digit number'
                : ' '
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email ID"
            value={formData.emailId}
            onChange={(e) => handleChange('emailId', e.target.value)}
            error={!!formData.emailId && !isValidEmail(formData.emailId)}
            helperText={
              formData.emailId && !isValidEmail(formData.emailId)
                ? 'Enter a valid email'
                : ' '
            }
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClick} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveClick}
            color="primary"
            disabled={!isEdited}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showAlert} onClose={() => setShowAlert(false)}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <p>{alertMessage}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAlert(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersonalDetailsEdit;
